import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ShareButton from "@/components/ShareButton";
import { ArrowLeft, Newspaper } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Post not found"));
  }, [slug]);

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />
      <div className="pt-28 px-6 sm:px-12 lg:px-20 max-w-3xl mx-auto pb-24" data-testid="blog-post-page">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:text-ember mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> All dispatches
        </Link>

        {err ? (
          <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-6 text-red-400" data-testid="blog-post-error">{err}</div>
        ) : !post ? (
          <p className="text-foreground/45">Loading…</p>
        ) : (
          <article className="space-y-6">
            <div className="flex items-center gap-3">
              <Newspaper className="w-5 h-5 text-gold" />
              <p className="text-[11px] uppercase tracking-widest text-foreground/45">
                {post.published_at ? new Date(post.published_at).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : ""}
                {post.author_name ? ` · ${post.author_name}` : ""}
              </p>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight" data-testid="blog-post-title">{post.title}</h1>

            <div className="flex flex-wrap gap-3 -mt-1">
              <ShareButton
                url={typeof window !== "undefined" ? `${window.location.origin}/blog/${post.slug}` : ""}
                title={`${post.title} · Cinevoya`}
                text={post.excerpt || "From the Cinevoya journal."}
                variant="outline"
                size="sm"
                className="border-white/15 hover:bg-white/5 h-9"
                testId="blog-share-btn"
              />
            </div>

            {post.image_url && (
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-ink/40 aspect-[16/9]" data-testid="blog-post-cover">
                <img src={`${API}${post.image_url}`} alt="" className="w-full h-full object-cover" />
              </div>
            )}

            <div
              className="prose-invert text-foreground/85 text-[16px] leading-relaxed whitespace-pre-wrap"
              data-testid="blog-post-body"
            >
              {post.body}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
